import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { DEFAULT_ORG_NAME } from './accountModel'
import { CashForecast, ProfitPerTruck } from './CfoCharts'
import { ChatPane } from './ChatPane'
import { CFO_THREAD } from './contacts'
import { FleetTopNav } from './FleetTopNav'
import { DESKTOP_WIDTH } from './OptionSection'
import { ProductTab, TAB_LABEL } from './prototypeNav'
import { TeamPanel } from './TeamPanel'

/** The agent's page: the chat takes the width, with only the team rail beside it. */
const CHAT_WIDE = 880
const TEAM_WIDTH = DESKTOP_WIDTH - CHAT_WIDE

/** A product's page: the chat gives the room back and docks at the edge. */
const CHAT_RAIL = 300
const NAV_RAIL = 48

/** Leaving the agent is a quick exit; coming back to it is an arrival. */
const EASING = 'ease-out motion-reduce:transition-none'
const AWAY_MS = 'duration-300'
const BACK_MS = 'duration-700'

/** As wide as the thread reads on the agent's page, before it docks. */
const THREAD_WIDE = 520

// Long enough to cover the rewrap on the way out, where the thread's cap drops
// to the rail width before the column has finished narrowing onto it.
const REVEAL_AWAY_MS = 150

/**
 * Laid out at a fixed width so it never rewraps while the column grows around
 * it. Greeked except on CFO, the one product this placement has switched on.
 */
function ProductPage({ tab }: { tab: ProductTab }) {
  return (
    <div className="flex flex-col gap-4 p-6" style={{ width: DESKTOP_WIDTH - NAV_RAIL - CHAT_RAIL }}>
      <h1 className="text-ink text-xl font-bold tracking-tight">{TAB_LABEL[tab]}</h1>
      {tab === ProductTab.CFO ? (
        <div className="flex flex-col gap-3">
          <CashForecast />
          <ProfitPerTruck />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {[100, 84, 92, 66, 78].map((pct) => (
            <div key={pct} className="bg-ink-50 h-3 rounded-sm" style={{ width: `${pct}%` }} />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Where the avatar goes when you leave the agent's page. Everything animates off
 * the chat column's own width, so the avatar travels because the room it is
 * centred in moves — the same thing that would happen in the real layout.
 */
export function AvatarHandoffMock({ className }: { className?: string }) {
  const [tab, setTab] = useState<ProductTab>(ProductTab.INSURANCE)
  const [onAgent, setOnAgent] = useState(true)
  const [bodyFaded, setBodyFaded] = useState(false)
  const reveal = useRef<number | undefined>(undefined)
  const glide = cn(EASING, onAgent ? BACK_MS : AWAY_MS)

  useEffect(() => () => window.clearTimeout(reveal.current), [])

  function goTo(next: boolean) {
    if (next === onAgent) return
    setOnAgent(next)
    window.clearTimeout(reveal.current)
    // Only the outbound trip hides the thread; arriving back, the fade read worse
    // than the reflow it was covering.
    if (next) {
      setBodyFaded(false)
      return
    }
    setBodyFaded(true)
    reveal.current = window.setTimeout(() => setBodyFaded(false), REVEAL_AWAY_MS)
  }

  return (
    <div className={cn('border-ink-100 flex flex-col overflow-hidden border bg-white', className)}>
      <FleetTopNav
        tab={tab}
        onSelect={(next) => {
          setTab(next)
          goTo(false)
        }}
        orgName={DEFAULT_ORG_NAME}
        symbolOnlyLogo
        agentActive={onAgent}
        onAgent={() => goTo(true)}
      />

      <div className="flex min-h-0 flex-1">
        <div
          className={cn('shrink-0 overflow-hidden transition-[width]', glide)}
          style={{ width: onAgent ? 0 : NAV_RAIL }}
        >
          <div className="border-ink-100 flex h-full flex-col gap-1 border-r p-2" style={{ width: NAV_RAIL }}>
            {[0, 1, 2].map((row) => (
              <div key={row} className="bg-ink-50 size-8 shrink-0 rounded-md" />
            ))}
          </div>
        </div>

        <div
          className={cn('min-w-0 flex-1 overflow-hidden transition-opacity', glide, onAgent && 'opacity-0')}
        >
          <ProductPage tab={tab} />
        </div>

        <ChatPane
          width={onAgent ? CHAT_WIDE : CHAT_RAIL}
          contentWidth={onAgent ? THREAD_WIDE : CHAT_RAIL}
          className={cn(
            'border-l transition-[width,border-color]',
            glide,
            onAgent ? 'border-transparent' : 'border-ink-100'
          )}
          hideAgentLabel
          people={[]}
          connect
          agentThread={CFO_THREAD}
          bodyFaded={bodyFaded}
        />

        <div
          className={cn('shrink-0 overflow-hidden transition-[width]', glide)}
          style={{ width: onAgent ? TEAM_WIDTH : 0 }}
        >
          <TeamPanel width={TEAM_WIDTH} />
        </div>
      </div>
    </div>
  )
}
